import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../ui/button';

interface HeaderProps {
  className?: string;
}

const HeaderContainer = styled.header<{ isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: ${({ theme }) => theme.transitions.default};
  background-color: ${({ isScrolled }) => 
    isScrolled 
      ? 'rgba(255, 255, 255, 0.9)' 
      : 'transparent'};
  backdrop-filter: ${({ isScrolled }) => 
    isScrolled ? 'blur(10px)' : 'none'};
  -webkit-backdrop-filter: ${({ isScrolled }) => 
    isScrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${({ isScrolled, theme }) => 
    isScrolled ? theme.shadows.sm : 'none'};
  border-bottom: ${({ isScrolled }) => 
    isScrolled ? '1px solid rgba(226, 232, 240, 0.8)' : 'none'};
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  max-width: 1280px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1.25rem 1.5rem;
  }
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.dark};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${({ theme }) => theme.fonts.heading};
  letter-spacing: -0.02em;
  text-decoration: none;
  position: relative;
  padding: 0.25rem 0;
  
  span {
    background: ${({ theme }) => theme.colors.gradient.cta};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }
`;

const MenuItems = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 250px;
    flex-direction: column;
    background-color: white;
    padding: 2rem;
    transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
    transition: transform 0.3s ease-in-out;
    box-shadow: ${({ theme }) => theme.shadows.lg};
    z-index: 101;
    justify-content: flex-start;
    gap: 1.5rem;
  }
`;

const NavLink = styled(Link)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray};
  text-decoration: none;
  position: relative;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.radii.md};
  transition: ${({ theme }) => theme.transitions.default};
  font-size: 0.9375rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.dark};
    background-color: rgba(0, 0, 0, 0.03);
  }
  
  &.active {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  width: 30px;
  height: 24px;
  position: relative;
  cursor: pointer;
  z-index: 102;
  padding: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
  
  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: ${({ theme }) => theme.colors.dark};
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transition: ${({ theme }) => theme.transitions.default};
    
    &:nth-child(1) {
      top: 0;
    }
    
    &:nth-child(2) {
      top: 10px;
    }
    
    &:nth-child(3) {
      top: 20px;
    }
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 100;
  }
`;

export const Header: React.FC<HeaderProps> = ({ className }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <HeaderContainer className={className} isScrolled={isScrolled}>
      <Nav>
        <Logo to="/">
          FylePrep
        </Logo>

        <MenuItems isOpen={isMenuOpen}>
          <NavLink to="/features">Features</NavLink>
          <NavLink to="/how-it-works">How It Works</NavLink>
          {/* <NavLink to="/pricing">Pricing</NavLink> */}
          {/* <NavLink to="/contact">Contact</NavLink> */}
          <Button as={Link} to="/waitlist" variant="primary" size="small">
            Join Waitlist
          </Button>
        </MenuItems>

        <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <span />
          <span />
          <span />
        </MobileMenuButton>

        {isMenuOpen && <Overlay isOpen={isMenuOpen} onClick={() => setIsMenuOpen(false)} />}
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
